/**
 * Authentication audit logging for security monitoring
 */

import { supabase } from './supabase';

export type AuthEventType = 
  | 'login_success'
  | 'login_failed'
  | 'logout'
  | 'session_expired'
  | 'session_refreshed'
  | 'password_reset_requested'
  | 'password_reset_completed'
  | 'registration_started'
  | 'registration_completed'
  | 'oauth_login'
  | 'suspicious_activity'
  | 'account_locked';

export interface AuthAuditEvent {
  id?: string;
  user_id?: string;
  event_type: AuthEventType;
  ip_address?: string;
  user_agent?: string;
  metadata?: Record<string, any>;
  created_at?: string;
}

class AuthAuditLogger {
  private queue: AuthAuditEvent[] = [];
  private flushInterval: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds

  constructor() {
    // Start batch processing
    this.startBatchProcessing();
  }

  /**
   * Log an authentication event
   */
  async log(event: AuthAuditEvent) {
    // Enrich event with client info
    const enrichedEvent: AuthAuditEvent = {
      ...event,
      ip_address: await this.getClientIP(),
      user_agent: navigator.userAgent,
      created_at: new Date().toISOString()
    };

    // Add to queue
    this.queue.push(enrichedEvent);
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔐 Auth Event: ${event.event_type}`, enrichedEvent);
    }

    // Flush if batch size reached
    if (this.queue.length >= this.BATCH_SIZE) {
      await this.flush();
    }
  }

  /**
   * Log a successful login
   */
  async logLogin(userId: string, method: 'password' | 'google' | 'magic_link', metadata?: any) {
    await this.log({
      user_id: userId,
      event_type: 'login_success',
      metadata: {
        method,
        ...metadata
      }
    });
  }

  /**
   * Log a failed login attempt
   */
  async logFailedLogin(email: string, reason: string, metadata?: any) {
    await this.log({
      event_type: 'login_failed',
      metadata: {
        email,
        reason,
        ...metadata
      }
    });

    // Check for suspicious activity
    await this.checkSuspiciousActivity(email);
  }

  /**
   * Log a logout
   */
  async logLogout(userId: string, reason?: string) {
    await this.log({
      user_id: userId,
      event_type: 'logout',
      metadata: {
        reason: reason || 'user_initiated'
      }
    });
  }

  /**
   * Log session events
   */
  async logSessionEvent(userId: string, eventType: 'session_expired' | 'session_refreshed', metadata?: any) {
    await this.log({
      user_id: userId,
      event_type: eventType,
      metadata
    });
  }

  /**
   * Check for suspicious activity patterns
   */
  private async checkSuspiciousActivity(email: string) {
    try {
      // Count recent failed login attempts
      const recentFailures = this.queue.filter(event => 
        event.event_type === 'login_failed' &&
        event.metadata?.email === email &&
        Date.now() - new Date(event.created_at!).getTime() < 15 * 60 * 1000 // Last 15 minutes
      );

      // If more than 5 failed attempts, log suspicious activity
      if (recentFailures.length >= 5) {
        await this.log({
          event_type: 'suspicious_activity',
          metadata: {
            email,
            reason: 'multiple_failed_login_attempts',
            attempt_count: recentFailures.length
          }
        });

        // TODO: Implement account locking or CAPTCHA
      }
    } catch (error) {
      console.error('Error checking suspicious activity:', error);
    }
  }

  /**
   * Get client IP address (best effort)
   */
  private async getClientIP(): Promise<string | undefined> {
    try {
      // Try to get IP from a public API
      const response = await fetch('https://api.ipify.org?format=json', {
        signal: AbortSignal.timeout(1000) // 1 second timeout
      });
      const data = await response.json();
      return data.ip;
    } catch {
      // Fallback to undefined if we can't get IP
      return undefined;
    }
  }

  /**
   * Start batch processing
   */
  private startBatchProcessing() {
    this.flushInterval = setInterval(() => {
      if (this.queue.length > 0) {
        this.flush();
      }
    }, this.FLUSH_INTERVAL);
  }

  /**
   * Flush queued events to database
   */
  private async flush() {
    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    try {
      // Try to create audit log table if it doesn't exist
      await this.ensureAuditTable();

      // Insert events
      const { error } = await supabase
        .from('auth_audit_logs')
        .insert(events);

      if (error) {
        console.error('Failed to save audit logs:', error);
        // Re-queue events on failure
        this.queue.unshift(...events);
      }
    } catch (error) {
      console.error('Error flushing audit logs:', error);
      // Re-queue events on failure
      this.queue.unshift(...events);
    }
  }

  /**
   * Ensure audit table exists (for development)
   */
  private async ensureAuditTable() {
    // This would normally be done via migrations
    // Including here for completeness
    const tableExists = await supabase
      .from('auth_audit_logs')
      .select('id')
      .limit(1);

    if (tableExists.error?.code === 'PGRST204') {
      console.log('Auth audit table does not exist, skipping audit logging');
      // In production, this table should be created via migrations
    }
  }

  /**
   * Get audit logs for a user
   */
  async getUserAuditLogs(userId: string, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('auth_audit_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return [];
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    
    // Final flush
    this.flush();
  }
}

// Export singleton instance
export const authAudit = new AuthAuditLogger();